import { useState } from "react";
import { useMutation, useQueries } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { AlertCircle, Check, Loader2, Wallet } from "lucide-react";
import { api, formatApiError } from "../../../api";
import { DESK } from "../../../constants/testIds";
import { useDeskData } from "../../../contexts/DeskDataContext";
import { formatCurrency } from "../../../lib/format";
import { fadeUp } from "../../../lib/motion";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { toast } from "../../../components/ui/sonner";

function todayIso() { return new Date().toISOString().slice(0, 10); }
function monthAgoIso() { const d = new Date(); d.setUTCDate(d.getUTCDate() - 30); return d.toISOString().slice(0, 10); }

// UX-FD-03 §1 SCR-P02-82 + highest-risk rule #3: `POST /payout-runs` references the exact
// attended session `_id`s it pays (`expense_engine.compute_payout_run`) and permanently excludes
// any session already referenced by a prior run — a same-period re-run returns a real 409
// NOTHING_TO_PAY (caught specifically below, shown as a calm "already settled" message, never a
// raw error or a silent double-pay). After a run, we auto-fetch each paid provider's real
// statement (`GET /providers/{id}/payout-statement`) — no providers-directory endpoint exists on
// this backend to pick a trainer up front, so the statement view is driven entirely by the
// run's own real `provider_ref`s rather than a fabricated lookup.
export default function PayoutRun() {
  const { branchId } = useDeskData();
  const reducedMotion = useReducedMotion();
  const [start, setStart] = useState(monthAgoIso());
  const [end, setEnd] = useState(todayIso());
  const [alreadyPaid, setAlreadyPaid] = useState(false);

  const runMutation = useMutation({
    mutationFn: () => api.post(
      "/payout-runs",
      { branch_id: branchId, period: { start, end: `${end}T23:59:59.999999+00:00` } },
      { headers: { "Idempotency-Key": crypto.randomUUID() } },
    ),
    onSuccess: () => setAlreadyPaid(false),
    onError: (e) => {
      if (e.response?.data?.code === "NOTHING_TO_PAY") setAlreadyPaid(true);
      else toast.error(formatApiError(e).message);
    },
  });

  const run = runMutation.data?.data;
  const statementQs = useQueries({
    queries: (run?.lines || []).map((line) => ({
      queryKey: ["desk-mgr-payout-statement", line.provider_ref],
      queryFn: async () => (await api.get(`/providers/${line.provider_ref}/payout-statement`)).data,
      enabled: !!run,
    })),
  });

  return (
    <div data-testid={DESK.mgrPayoutPage} className="space-y-6 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Trainer payout run</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Computes commissions owed from attended sessions for the period. A settled period can never be paid twice.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium" style={{ color: "#68686E" }}>From</label>
            <Input data-testid={DESK.mgrPayoutPeriodStartInput} type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium" style={{ color: "#68686E" }}>To</label>
            <Input data-testid={DESK.mgrPayoutPeriodEndInput} type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>
        <Button
          data-testid={DESK.mgrPayoutRunButton}
          disabled={runMutation.isPending}
          onClick={() => { setAlreadyPaid(false); runMutation.mutate(); }}
          className="w-full text-white gap-2" style={{ background: "#D6202B" }}
        >
          {runMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : <><Wallet size={14} /> Run payout for this period</>}
        </Button>
        {alreadyPaid && (
          <p data-testid={DESK.mgrPayoutAlreadyPaidMessage} className="flex items-center gap-1.5 text-sm font-medium" style={{ color: "var(--scarlet-neg)" }}>
            <AlertCircle size={14} /> Nothing to pay — every attended session in this period is already covered by a prior run.
          </p>
        )}
      </motion.div>

      {run && (
        <motion.div {...fadeUp(reducedMotion, 0.08)} data-testid={DESK.mgrPayoutResult} className="rounded-[14px] overflow-hidden" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <p className="flex items-center gap-1.5 text-sm font-semibold px-4 py-3" style={{ color: "var(--scarlet-pos)", borderBottom: "1px solid #EBEAE5" }}>
            <Check size={15} /> Paid {formatCurrency(run.total)} across {run.lines.length} trainer(s)
          </p>
          {run.lines.map((line, i) => (
            <div key={line.provider_ref} data-testid={DESK.mgrPayoutStatementRow(line.provider_ref)} className="px-4 py-3" style={{ borderBottom: "1px solid #F4F3EF" }}>
              <div className="flex items-center justify-between text-sm">
                <span style={{ color: "#141416" }}>Trainer <span className="font-mono text-xs" style={{ color: "#68686E" }}>{line.provider_ref}</span> — {line.sessions.length} session(s)</span>
                <span className="font-semibold" style={{ color: "#141416" }}>{formatCurrency(line.commission_owed)}</span>
              </div>
              {statementQs[i]?.data && (
                <p className="text-xs mt-1" style={{ color: "#68686E" }}>Lifetime paid to this trainer: {formatCurrency(statementQs[i].data.total_paid)} across {statementQs[i].data.runs.length} run(s).</p>
              )}
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
}
